/**
 * SessionScopeGuard — keeps the consumer funnel's auth from evicting the staff
 * session (and vice-versa). Mounted once inside the router.
 *
 * Rules, per route change and per auth state change:
 *  1. A session that signs IN while you are on a given surface is stashed under
 *     that surface's scope (and re-stashed on every token refresh, so the vault
 *     always holds the newest refresh token).
 *  2. Landing on a surface whose active session belongs to the OTHER scope
 *     restores that surface's stashed session, if it has one.
 *
 * See src/lib/session-vault.ts for why this is a vault rather than a second client.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  readVault,
  restoreScope,
  scopeForPath,
  writeVault,
  type SessionScope,
} from "@/lib/session-vault";

export default function SessionScopeGuard() {
  const { pathname } = useLocation();
  const scopeRef = useRef<SessionScope>(scopeForPath(pathname));
  scopeRef.current = scopeForPath(pathname);

  // (1) Remember the session that is active on the current surface.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session?.user) return;
      if (event === "SIGNED_OUT" || event === "PASSWORD_RECOVERY") return;
      // INITIAL_SESSION included on purpose: a session rehydrated from storage
      // on a staff route is exactly the one we must be able to give back later.
      writeVault(scopeRef.current, session);
    });
    return () => subscription.unsubscribe();
  }, []);

  // (2) On surface change, put the right session back in the client.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const scope = scopeForPath(pathname);
      const stash = readVault(scope);
      if (!stash) return;
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      const activeUser = data.session?.user?.id ?? null;
      if (activeUser === stash.user_id) return;
      // Either nothing is signed in here, or the active identity belongs to the
      // other surface — hand this surface its own session back.
      await restoreScope(scope);
    })();
    return () => { cancelled = true; };
  }, [pathname]);

  return null;
}
